import { pythonApiClient } from "./python-http-client.service";
import axios from "axios";
import {
  ChatbotConfiguration,
  FlowConnector,
  FlowDefinition,
  FlowDocument,
  FlowValidationResult,
  PreviewResponse,
} from "../models/flow.model";

const businessPath = (businessId: string) =>
  `/api/flows/businesses/${encodeURIComponent(businessId)}`;

export const flowApiErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : "Something went wrong.";
  }

  const detail = error.response?.data?.detail;
  if (Array.isArray(detail?.issues)) {
    return detail.issues
      .map((issue: { message?: string }) => issue.message)
      .filter(Boolean)
      .join(" ");
  }

  if (error.response?.status === 404 && (!detail || detail === "Not Found")) {
    return "The Flow API is unavailable on the configured backend. Deploy the backend version that includes /api/flows, then try again.";
  }

  if (error.response?.status === 502 || error.response?.status === 503) {
    return "The chatbot service is temporarily unavailable. Please try again shortly.";
  }

  return typeof detail === "string"
    ? detail
    : error.message || "Something went wrong.";
};

export const flowApi = {
  configuration: async (businessId: string) =>
    (await pythonApiClient.get<ChatbotConfiguration>(`${businessPath(businessId)}/configuration`)).data,

  updateConfiguration: async (businessId: string, value: ChatbotConfiguration) =>
    (await pythonApiClient.patch<ChatbotConfiguration>(`${businessPath(businessId)}/configuration`, value)).data,

  connectors: async (businessId: string) =>
    (await pythonApiClient.get<{ items: FlowConnector[] }>(`${businessPath(businessId)}/connectors`)).data.items,

  list: async (businessId: string) =>
    (await pythonApiClient.get<{ items: FlowDocument[] }>(businessPath(businessId))).data.items,

  get: async (businessId: string, flowId: string, version?: number) =>
    (await pythonApiClient.get<FlowDocument>(`${businessPath(businessId)}/${flowId}`, { params: { version } })).data,

  create: async (businessId: string, name: string, definition: FlowDefinition) =>
    (await pythonApiClient.post<FlowDocument>(businessPath(businessId), { name, description: "", definition })).data,

  update: async (businessId: string, flow: FlowDocument) =>
    (await pythonApiClient.put<FlowDocument>(
      `${businessPath(businessId)}/${flow.flowId}/versions/${flow.version}`,
      { name: flow.name, description: flow.description, definition: flow.definition, revision: flow.revision },
    )).data,

  validate: async (businessId: string, flow: FlowDocument) =>
    (await pythonApiClient.post<FlowValidationResult>(
      `${businessPath(businessId)}/${flow.flowId}/versions/${flow.version}/validate`,
    )).data,

  publish: async (businessId: string, flow: FlowDocument) =>
    (await pythonApiClient.post<FlowDocument>(
      `${businessPath(businessId)}/${flow.flowId}/versions/${flow.version}/publish`,
    )).data,

  newVersion: async (businessId: string, flow: FlowDocument) =>
    (await pythonApiClient.post<FlowDocument>(
      `${businessPath(businessId)}/${flow.flowId}/versions/${flow.version}/new-version`,
    )).data,

  clone: async (businessId: string, flow: FlowDocument) =>
    (await pythonApiClient.post<FlowDocument>(
      `${businessPath(businessId)}/${flow.flowId}/versions/${flow.version}/clone`, {},
    )).data,

  preview: async (
    definition: FlowDefinition,
    state: Record<string, any>,
    message?: Record<string, any>,
    connectorFixtures: Record<string, unknown> = {},
  ) => (await pythonApiClient.post<PreviewResponse>("/api/flows/preview", {
    definition, state, message, connectorFixtures,
  })).data,

  createTemplate: async (name: string, definition: FlowDefinition) =>
    (await pythonApiClient.post<Record<string, any>>("/api/flows/templates", {
      name, description: "Reusable chatbot flow template", definition,
    })).data,
};
